import _ from 'lodash'
import { useMemo } from 'react'
import useDebounce from './useDebounce'
import useFetch from './useFetch'

export interface UseAccountOptions {
  limit?: number,
  offset?: number,
  adminListing?: boolean,
  sortBy?: string[]
  ids?: string[] | Set<string>
}

const defaultOptions = {
  limit: 10,
  offset: 0,
  adminListing: false,
}

const idNotNullQuery = { field: 'id', operand: 'NEQ', value: null }
const valueContainsQuery = (field: string, value: string) => ({ field, operand: 'CONTAINS', value })
const idsInQuery = (value: string[] | Set<string>) => {
  if (value instanceof Set) {
    return { field: 'id', operand: 'IN', value: Array.from(value) }
  } else {
    return { field: 'id', operand: 'IN', value }
  }
}

const useAccounts = (searchText: string, options: UseAccountOptions = {}, debounceMs?: number) => {
  const debouncedSearchText = useDebounce(searchText, debounceMs)

  const searchQuery = useMemo(
    () => {
      const hasSearchText = debouncedSearchText?.length > 0
      const hasIdFilter = options?.ids instanceof Set || Array.isArray(options?.ids)

      if (hasSearchText && hasIdFilter) {
        return [
          [idsInQuery(options.ids), valueContainsQuery('name', debouncedSearchText)],
          [idsInQuery(options.ids), valueContainsQuery('org_name', debouncedSearchText)]
        ]
      } else if (hasSearchText) {
        return [
          [idNotNullQuery, valueContainsQuery('name', debouncedSearchText)],
          [idNotNullQuery, valueContainsQuery('org_name', debouncedSearchText)]
        ]
      } else if (hasIdFilter) {
        return [[idsInQuery(options.ids)]]
      } else {
        return [[idNotNullQuery]]
      }
    },
    [debouncedSearchText, options.ids]
  )

  const fetchOptions: RequestInit = useMemo(
    () => ({
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...defaultOptions,
        ...options,
        criteria: searchQuery,
      })
    }),
    [searchQuery, options.limit, options.offset, options.adminListing, options.sortBy]
  )

  const { loading, error, data } = useFetch(
    '/accountservice/api/v3/accounts/search',
    fetchOptions,
    [fetchOptions]
  )

  if (data?.accounts?.length > 0) {
    // add alias: pCode -> pcode
    data.accounts.forEach(account => account.pCode = account.pcode)
  }

  return { loading, error, data }
}

export default useAccounts
